import React from "react";
import { useNavigate } from "react-router-dom";
import { setUxMode } from "../../lib/uxMode";
import {
  Megaphone, Tv, Monitor, Layers, Mail, Mailbox, Route, Vote, Database, Users2,
  FileText, MapPinned, Compass, ListChecks, CalendarDays, HeartHandshake, Trophy,
  Building2, Briefcase, Palette, FileCheck2, QrCode, HandCoins, LayoutTemplate,
  Globe, Users, Receipt, TrendingUp, MessageSquare, Clapperboard, Sparkles, Wand2,
  Send, BrainCircuit, Landmark, Activity, Gauge, LineChart, ShieldCheck, ScrollText,
  Wallet, Settings, HelpCircle, SlidersHorizontal, Radio,
} from "lucide-react";
import { toast } from "sonner";

const GROUPS = [
  {
    label: "Advertising Tools",
    tools: [
      { icon: Megaphone, name: "Campaigns", desc: "Every campaign you've built, live or draft.", to: "/campaigns" },
      { icon: Tv, name: "CTV Campaigns", desc: "Build a streaming-TV campaign step by step.", to: "/campaigns/ctv/new" },
      { icon: Monitor, name: "CTV Voter Planner", desc: "Forecast household reach across your district.", to: "/planner/ctv" },
      { icon: Layers, name: "Media Plans", desc: "Reusable media mixes you can apply to any campaign.", to: "/media-plans" },
      { icon: Mail, name: "Email Campaigns", desc: "Send branded email from your own domain.", to: "/email-campaigns" },
      { icon: Mailbox, name: "Direct Mail", desc: "Design postcards with QR attribution.", to: "/direct-mail" },
      { icon: Route, name: "Journeys", desc: "Multi-step email, mail and CTV sequences.", to: "/journeys" },
    ],
  },
  {
    label: "Targeting",
    tools: [
      { icon: Vote, name: "Target Voters", desc: "Political audience discovery, AI-assisted.", to: "/target-voters" },
      { icon: Database, name: "Political Data", desc: "243+ real political data segments.", to: "/political-data" },
      { icon: Users2, name: "My Audiences", desc: "Audiences you've saved or activated.", to: "/audiences" },
      { icon: FileText, name: "First-Party Data", desc: "Upload and activate your voter file.", to: "/first-party" },
      { icon: MapPinned, name: "District & Geo", desc: "Geofences, districts and location targeting.", to: "/geo" },
      { icon: Compass, name: "Other Targeting", desc: "Lifestyle, contextual and in-market segments.", to: "/other-targeting" },
    ],
  },
  {
    label: "Campaign Operations",
    tools: [
      { icon: ListChecks, name: "Tasks & Approvals", desc: "Approval chains, tasks and notifications.", to: "/operations/advanced" },
      { icon: CalendarDays, name: "Events", desc: "Public events with RSVP and shift signup.", to: "/events" },
      { icon: HeartHandshake, name: "Volunteers", desc: "Your volunteer roster, skills and availability.", to: "/volunteers" },
      { icon: Trophy, name: "Volunteer Leaderboard", desc: "Who's doing the most for the campaign.", to: "/volunteers/leaderboard" },
      { icon: Building2, name: "Race Portfolio", desc: "Every entity you manage, side by side.", to: "/portfolio" },
      { icon: Briefcase, name: "Agency", desc: "Consolidated client view for consultants.", to: "/agency" },
      { icon: Palette, name: "Brand Library", desc: "Logos, slogans, talking points, approved claims.", to: "/brand-library" },
      { icon: FileCheck2, name: "Claims Vault", desc: "Sourced, approved factual claims.", to: "/claims-vault" },
      { icon: QrCode, name: "QR Codes", desc: "Trackable QR codes for print and events.", to: "/qr-codes" },
      { icon: Megaphone, name: "Political Ad Library", desc: "Live public ad intelligence.", to: "/ad-library" },
      { icon: Vote, name: "Election Night", desc: "Official results entry and race outcome.", to: "/election-night" },
    ],
  },
  {
    label: "Fundraising",
    tools: [
      { icon: HandCoins, name: "Dashboard", desc: "Fundraising at a glance.", to: "/fundraising" },
      { icon: HeartHandshake, name: "Campaigns", desc: "Goal-based fundraising campaigns.", to: "/fundraising/campaigns" },
      { icon: LayoutTemplate, name: "Landing Pages", desc: "AI-built donation landing pages.", to: "/fundraising/landing-pages" },
      { icon: Globe, name: "Microsites", desc: "Shareable public mini-sites.", to: "/fundraising/microsites" },
      { icon: Users, name: "Donors", desc: "Your donor CRM, auto-built from contributions.", to: "/fundraising/donors" },
      { icon: Receipt, name: "Transactions", desc: "Every contribution, refund and receipt.", to: "/fundraising/transactions" },
      { icon: TrendingUp, name: "Source Economics", desc: "Which sources raise the most, net of cost.", to: "/fundraising/economics" },
      { icon: MessageSquare, name: "Text-to-Give", desc: "SMS keywords that reply with donate links.", to: "/fundraising/sms" },
    ],
  },
  {
    label: "Creative",
    tools: [
      { icon: Clapperboard, name: "Political Ad Studio", desc: "AI video, voiceover and banner production.", to: "/ad-studio" },
      { icon: Sparkles, name: "Creative Refreshes", desc: "Weekly AI-generated creative variants.", to: "/ad-studio/refreshes" },
      { icon: Sparkles, name: "AI Campaign Assistant", desc: "Conversational campaign strategy help.", to: "/ai-assistant" },
      { icon: Wand2, name: "AI Creative Studio", desc: "Generate ad creative with AI.", to: "/creative-studio" },
      { icon: LayoutTemplate, name: "AI Landing Page Builder", desc: "Landing pages written and designed by AI.", to: "/ai-landing-page" },
      { icon: Send, name: "Creative Library", desc: "Every asset you've produced.", to: "/creatives" },
    ],
  },
  {
    label: "Intelligence",
    tools: [
      { icon: BrainCircuit, name: "Political Intelligence", desc: "Polling, issues, strategy and live feeds.", to: "/political-intelligence" },
      { icon: Landmark, name: "Race Intelligence", desc: "Your race — candidates, finance, sources.", to: "/race-intelligence" },
      { icon: Activity, name: "Polling & Sentiment", desc: "Favorability trends and issue sentiment.", to: "/intelligence" },
      { icon: Vote, name: "Opponent Intelligence", desc: "Share of voice and public activity.", to: "/opponent" },
      { icon: Landmark, name: "Midterm Directory", desc: "2,500+ live FEC candidates.", to: "/midterms" },
    ],
  },
  {
    label: "Measurement",
    tools: [
      { icon: Gauge, name: "Optimization Agent", desc: "AI flags pacing, fatigue and reach gaps.", to: "/optimize" },
      { icon: LineChart, name: "Advertising Reports", desc: "Delivery, spend and results in detail.", to: "/reports" },
      { icon: Tv, name: "CTV Reports", desc: "Streaming TV performance.", to: "/reports/ctv" },
    ],
  },
  {
    label: "Compliance",
    tools: [
      { icon: ShieldCheck, name: "Compliance Center", desc: "Rules, disclaimers, verification, reviews.", to: "/compliance" },
      { icon: ScrollText, name: "Political Audit Vault", desc: "Exportable audit trail of every action.", to: "/audit" },
    ],
  },
  {
    label: "Account",
    tools: [
      { icon: Building2, name: "Political Organizations", desc: "Entities, brand kits and memberships.", to: "/organizations" },
      { icon: Sparkles, name: "Agent Autopilot", desc: "Set the limits AI must respect.", to: "/autopilot" },
      { icon: Users, name: "Team", desc: "Invite staff and manage roles.", to: "/team" },
      { icon: Radio, name: "War Room", desc: "Daily brief, countdown and alerts.", to: "/war-room" },
      { icon: Wallet, name: "Billing", desc: "Your plan, invoices and AI usage.", to: "/billing" },
      { icon: Settings, name: "Users & Settings", desc: "Access requests and account admin.", to: "/admin" },
      { icon: HelpCircle, name: "Help & Glossary", desc: "Plain-English explanations of everything.", to: "/help" },
    ],
  },
];

export default function MoreTools() {
  const navigate = useNavigate();

  const goPro = async () => {
    await setUxMode("pro");
    toast.success("Switched to Pro Mode — full sidebar unlocked");
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="more-tools">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-2">More Tools</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">The full TotalReach toolkit</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-2xl">
            Everything under the hood — same data, same campaigns, full control. Prefer this all the time?
          </p>
        </div>
        <button onClick={goPro} data-testid="more-tools-switch-pro"
          className="inline-flex items-center gap-2 border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/10 font-semibold rounded-full px-5 py-2.5 text-sm transition-colors">
          <SlidersHorizontal className="w-4 h-4" /> Switch to Pro Mode
        </button>
      </div>

      {GROUPS.map((g) => (
        <div key={g.label} className="mb-8" data-testid={`tools-group-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">{g.label}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {g.tools.map((t) => (
              <button key={t.to + t.name} onClick={() => navigate(t.to)}
                data-testid={`tool-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group text-left rounded-xl border border-white/10 bg-white/[0.02] hover:border-cyan-400/30 hover:bg-cyan-400/[0.03] transition-colors p-4">
                <div className="flex items-start gap-3">
                  <t.icon className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{t.name}</div>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{t.desc}</p>
                    <span className="text-[10px] text-slate-600 group-hover:text-cyan-300 transition-colors mt-1 inline-block">Open tool →</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
